// appInsights.js
// Initializes the Application Insights JavaScript SDK for frontend telemetry.
//
// Set REACT_APP_APPINSIGHTS_CONNECTION_STRING in your build environment
// (Azure App Service > Configuration, or the CI/CD pipeline variables).
// When the variable is absent the module is a no-op, so local dev without
// the variable set will not throw any errors.

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const connectionString = "InstrumentationKey=750d86e3-0933-4409-9163-490984107e43;IngestionEndpoint=https://germanywestcentral-0.in.applicationinsights.azure.com/;LiveEndpoint=https://germanywestcentral.livediagnostics.monitor.azure.com/;ApplicationId=335bbc47-45ab-4fb1-ac9b-34a92b5f401e";

let appInsights = null;

// Third-party tracking domains that are routinely blocked by ad blockers.
// Excluding them prevents App Insights from flooding with status-0 failures
// that are outside our control and not indicative of real application errors.
const EXCLUDED_DOMAINS = [
  'google-analytics.com',
  'googletagmanager.com',
  'connect.facebook.net',
  'facebook.com/tr',
  'analytics.google.com',
  'doubleclick.net',
];

if (connectionString) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      // Page view tracking is handled manually in RouteTracker so we can
      // include the referrer (previous page) as a custom property.
      enableAutoRouteTracking: false,
      // Track outgoing fetch/XHR requests automatically.
      disableFetchTracking: false,
      disableAjaxTracking: false,
      // Correlate frontend requests with backend telemetry via request headers.
      enableCorsCorrelation: true,
      // Suppress dependency tracking for third-party tracking domains so that
      // ad-blocker-induced status-0 failures don't pollute our telemetry.
      correlationHeaderExcludedDomains: EXCLUDED_DOMAINS,
    },
  });
  // Paths on otherwise-legitimate domains that belong to ad/tracking networks.
  // We can't exclude the whole domain, so we match by path prefix instead.
  const EXCLUDED_PATHS = [
    'www.google.com/pagead/',
    'www.google.com/ccm/',
  ];

  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.baseType === 'RemoteDependencyData') {
      const target = envelope.baseData?.target || '';
      if (EXCLUDED_DOMAINS.some(domain => target.includes(domain))) {
        return false; // drop the telemetry item
      }
      if (EXCLUDED_PATHS.some(path => target.includes(path))) {
        return false; // drop ad/tracking calls on shared domains
      }
    }
  });
  appInsights.loadAppInsights();
}

/**
 * Track a page view.
 * @param {string} path      Current page path (e.g. "/product/pouf-boucle-white")
 * @param {string} referrer  Previous page path – shows how the user arrived here
 */
export function trackPageView(path, referrer) {
  if (!appInsights) return;
  appInsights.trackPageView({
    name: path,
    uri: path,
    properties: {
      referrer: referrer || '',
    },
  });
}

/**
 * Track a custom event.
 * @param {string} name
 * @param {object} properties
 */
export function trackEvent(name, properties = {}) {
  if (!appInsights) return;
  appInsights.trackEvent({ name }, properties);
}

export default { trackPageView, trackEvent };
